import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  notFound,
  Outlet,
  redirect,
  useParams,
} from '@tanstack/react-router';
import { isApiError } from '../api/client';
import { accessQuery } from '../api/queries';
import { isInvoiceKind, type InvoiceKind } from '../api/types';
import { LoginPage } from '../features/auth/LoginPage';
import { RegisterPage } from '../features/auth/RegisterPage';
import { ChooseOrgPage } from '../features/orgs/ChooseOrgPage';
import { CreateOrgPage } from '../features/orgs/CreateOrgPage';
import { OrgHomePage } from '../features/orgs/OrgHomePage';
import { BatchPage } from '../features/sales/batch/BatchPage';
import { criteriaOf } from '../features/sales/batch/criteria';
import { InvoiceDetailPage } from '../features/sales/InvoiceDetailPage';
import { InvoiceEditorPage } from '../features/sales/InvoiceEditorPage';
import { InvoiceListPage, parseListSearch } from '../features/sales/InvoiceListPage';
import { ReceiptDetailPage } from '../features/receipts/ReceiptDetailPage';
import {
  ReceiptEditorPage,
  parseReceiptEditorSearch,
} from '../features/receipts/ReceiptEditorPage';
import { ReceiptListPage, parseReceiptListSearch } from '../features/receipts/ReceiptListPage';
import { NotBuiltPage } from '../features/notbuilt/NotBuilt';
import { NotFoundPage, OrgNotFoundPage, RouteErrorPage } from './ErrorPages';
import { loadMe, safeRedirect } from './session';
import { isModuleVisible, type ModuleId } from './shell/hubs';
import { AppShell } from './shell/AppShell';

/**
 * Code-based route tree (no codegen). Pages for new modules are added under `orgRoute`, and their
 * top-navigation entries in AppShell's module registry.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
  errorComponent: RouteErrorPage,
});

interface LoginSearch {
  redirect?: string;
  reason?: 'expired';
}

const signedInGuard = async ({ context }: { context: RouterContext }) => {
  if (await loadMe(context.queryClient)) throw redirect({ to: '/' });
};

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login',
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const target = safeRedirect(search.redirect);
    return {
      ...(target ? { redirect: target } : {}),
      ...(search.reason === 'expired' ? { reason: 'expired' as const } : {}),
    };
  },
  beforeLoad: signedInGuard,
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'register',
  beforeLoad: signedInGuard,
  component: RegisterPage,
});

/** Everything below needs a session; without one the user is sent to sign in and back. */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async ({ context, location }) => {
    const me = await loadMe(context.queryClient);
    if (!me) throw redirect({ to: '/login', search: { redirect: location.href } });
    return { me };
  },
  component: Outlet,
});

const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    const organizations = context.me.organizations;
    const only = organizations.length === 1 ? organizations[0] : undefined;
    if (only) throw redirect({ to: '/orgs/$orgId', params: { orgId: only.id } });
    throw redirect({ to: organizations.length === 0 ? '/orgs/new' : '/orgs' });
  },
});

const orgsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'orgs',
  component: Outlet,
});

const orgsIndexRoute = createRoute({
  getParentRoute: () => orgsRoute,
  path: '/',
  component: ChooseOrgPage,
});

const orgsNewRoute = createRoute({
  getParentRoute: () => orgsRoute,
  path: 'new',
  component: CreateOrgPage,
});

function OrgLayout() {
  const { orgId } = useParams({ strict: false });
  return (
    <AppShell {...(orgId ? { orgId } : {})}>
      <Outlet />
    </AppShell>
  );
}

/** The organization always comes from the URL; the API answers 404 to non-members. */
const orgRoute = createRoute({
  getParentRoute: () => orgsRoute,
  path: '$orgId',
  beforeLoad: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(accessQuery(params.orgId));
    } catch (error) {
      if (isApiError(error) && (error.status === 404 || error.status === 422)) throw notFound();
      throw error;
    }
  },
  component: OrgLayout,
  notFoundComponent: () => (
    <AppShell>
      <OrgNotFoundPage />
    </AppShell>
  ),
});

const orgIndexRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/',
  component: OrgHomePage,
});

const invoicesRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'sales/invoices',
  validateSearch: parseListSearch,
  component: InvoiceListPage,
});

const invoiceNewRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'sales/invoices/new',
  // Carry the editor's entry params: the kind (export invoice or a Block 11 note) and, for a note,
  // the invoice it starts from. The domestic invoice `i` is the default, so it is not kept in the URL.
  validateSearch: (search: Record<string, unknown>): { kind?: InvoiceKind; from?: string } => {
    const kind =
      typeof search.kind === 'string' && isInvoiceKind(search.kind) && search.kind !== 'i'
        ? search.kind
        : undefined;
    const from = typeof search.from === 'string' ? search.from : undefined;
    return { ...(kind ? { kind } : {}), ...(from ? { from } : {}) };
  },
  component: () => <InvoiceEditorPage mode="new" />,
});

/**
 * The batch page (WP-1b.3, MF-INV-008): its own route under the register, declared before the
 * document route so `batch` is never read as a document id. It carries the list's criteria.
 */
const invoiceBatchRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'sales/invoices/batch',
  validateSearch: criteriaOf,
  component: BatchPage,
});

const invoiceDetailRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'sales/invoices/$docId',
  component: InvoiceDetailPage,
});

const invoiceEditRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'sales/invoices/$docId/edit',
  component: () => <InvoiceEditorPage mode="edit" />,
});

/**
 * Planned pages at their final routes (IA rule 1). They stay in the route tree so the typed links of
 * the IA remain valid, but a module that is not in `USER_VISIBLE_MODULES` (shell/hubs.ts) is hidden:
 * its beforeLoad throws notFound() and the deep link answers the same clean 404 a non-member gets —
 * never a "coming soon" / "not built yet" page. Re-enabling a module is adding its id to that switch
 * and replacing the component below with the real page.
 */
const notBuilt = <TPath extends string>(path: TPath, id: ModuleId) =>
  createRoute({
    getParentRoute: () => orgRoute,
    path,
    beforeLoad: () => {
      if (!isModuleVisible(id)) throw notFound();
    },
    component: () => <NotBuiltPage id={id} />,
  });
const receiptsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'sales/receipts',
  validateSearch: parseReceiptListSearch,
  component: ReceiptListPage,
});

const receiptNewRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'sales/receipts/new',
  validateSearch: parseReceiptEditorSearch,
  component: () => <ReceiptEditorPage mode="new" />,
});

const receiptDetailRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'sales/receipts/$receiptId',
  component: ReceiptDetailPage,
});

const receiptEditRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'sales/receipts/$receiptId/edit',
  component: () => <ReceiptEditorPage mode="edit" />,
});

const paymentsRoute = notBuilt('sales/payments', 'paymentTracking');
const incomingRoute = notBuilt('incoming/documents', 'receivedDocuments');
const filingRoute = notBuilt('filing', 'filing');
const filingSalesRoute = notBuilt('filing/sales', 'filingSales');
const filingPurchasesRoute = notBuilt('filing/purchases', 'filingPurchases');
const filingVatRoute = notBuilt('filing/vat', 'filingVat');
const filingWithholdingRoute = notBuilt('filing/withholding', 'filingWithholding');
const filingFilesRoute = notBuilt('filing/files', 'filingFiles');
const buyersRoute = notBuilt('records/customers', 'buyers');
const suppliersRoute = notBuilt('records/suppliers', 'suppliers');
const itemsRoute = notBuilt('records/items', 'items');
const orgSetupRoute = notBuilt('organization/setup', 'orgSetup');
const teamRoute = notBuilt('organization/team', 'team');
const importsRoute = notBuilt('organization/imports', 'imports');

const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  authedRoute.addChildren([
    indexRoute,
    orgsRoute.addChildren([
      orgsIndexRoute,
      orgsNewRoute,
      orgRoute.addChildren([
        orgIndexRoute,
        invoicesRoute,
        invoiceNewRoute,
        invoiceBatchRoute,
        invoiceDetailRoute,
        invoiceEditRoute,
        receiptsRoute,
        receiptNewRoute,
        receiptDetailRoute,
        receiptEditRoute,
        paymentsRoute,
        incomingRoute,
        filingRoute,
        filingSalesRoute,
        filingPurchasesRoute,
        filingVatRoute,
        filingWithholdingRoute,
        filingFilesRoute,
        buyersRoute,
        suppliersRoute,
        itemsRoute,
        orgSetupRoute,
        teamRoute,
        importsRoute,
      ]),
    ]),
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    defaultNotFoundComponent: NotFoundPage,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
